import type { Aircraft, Airline, KnownAirport } from '@trippath/shared';

/** How an airline is said: `Etihad Airways (EY/ETD)`. */
export function airlineLabel(airline: Airline): string {
  return `${airline.name} (${airline.code}${airline.icao === '' ? '' : `/${airline.icao}`})`;
}

/**
 * The airline's code in what is typed: `Finnair (AY/FIN)` or `ay` is `AY`, a name known its
 * code; none for anything else.
 */
export function airlineCode(typed: string, airlines: readonly Airline[]): string | null {
  const text = typed.trim();
  const said = /\(([A-Z0-9]{2})(?:\/[A-Z]{3})?\)$/.exec(text)?.[1];
  if (said !== undefined) return said;
  if (/^[A-Z0-9]{2}$/i.test(text)) return text.toUpperCase();
  const named = airlines.find((a) => a.name.toLowerCase() === text.toLowerCase());
  return named ? named.code : null;
}

/** The airline a flight number says: `AY100` is Finnair's. */
export function airlineOfFlight(number: string, airlines: readonly Airline[]): Airline | null {
  const code = number.trim().slice(0, 2).toUpperCase();
  return airlines.find((a) => a.code === code) ?? null;
}

/** The airlines that fit what is typed, by name or code: a code said first. */
export function findAirlines(typed: string, airlines: readonly Airline[]): Airline[] {
  const text = typed.trim().toLowerCase();
  if (text === '') return [];
  const code = (a: Airline) => a.code.toLowerCase() === text || a.icao.toLowerCase() === text;
  return airlines
    .filter((a) => code(a) || a.name.toLowerCase().includes(text))
    .toSorted((a, b) => Number(code(b)) - Number(code(a)));
}

/** How an aircraft type is said: `Airbus A380-800 (A388)`. */
export function aircraftLabel(type: Aircraft): string {
  return type.icao === '' ? type.name : `${type.name} (${type.icao})`;
}

/** The aircraft types that fit what is typed, by name or code: a code said first. */
export function findAircraft(typed: string, types: readonly Aircraft[]): Aircraft[] {
  const text = typed.trim().toLowerCase();
  if (text === '') return [];
  const code = (a: Aircraft) => a.icao.toLowerCase() === text || a.iata.toLowerCase() === text;
  return types
    .filter((a) => code(a) || aircraftLabel(a).toLowerCase().includes(text))
    .toSorted((a, b) => Number(code(b)) - Number(code(a)));
}

/** How an airport is offered: `AUH · Zayed International Airport, Abu Dhabi`. */
export function airportLabel(airport: KnownAirport): string {
  return `${airport.code} · ${airport.name}, ${airport.city}`;
}

/** The airports that fit what is typed, by code, city or name: its code said first, then its city. */
export function findAirports(typed: string, airports: readonly KnownAirport[]): KnownAirport[] {
  const text = typed.trim().toLowerCase();
  if (text === '') return [];
  const rank = (a: KnownAirport) =>
    a.code.toLowerCase() === text ? 2 : a.city.toLowerCase().startsWith(text) ? 1 : 0;
  return airports
    .filter((a) => rank(a) > 0 || a.name.toLowerCase().includes(text))
    .toSorted((a, b) => rank(b) - rank(a));
}

/**
 * The ICAO type code of an aircraft as a ticket says it: `Airbus A320neo (A20N)` or `A20N` is
 * `A20N`, a name known is its code; `null` for anything else.
 */
export function typeCodeOf(aircraft: string, types: readonly Aircraft[]): string | null {
  const text = aircraft.trim();
  const said = /\(([A-Z0-9]{2,4})\)$/.exec(text)?.[1];
  if (said !== undefined) return said;
  if (/^[A-Z][A-Z0-9]{1,3}$/.test(text)) return text;
  const lower = text.toLowerCase();
  // By its name, else by its family's: `Airbus A350` is the first A350 known, the A350-900.
  const named =
    types.find((a) => a.icao !== '' && a.name.toLowerCase() === lower) ??
    types.find(
      (a) => a.icao !== '' && lower.length > 3 && a.name.toLowerCase().startsWith(lower),
    );
  return named ? named.icao : null;
}
